import * as React from 'react';

// API
import apibridge from '../../apibridge';

// UTILS
import { validationErrorsToString } from '../../utils';
import { useTabState } from '../../hooks/useTabState';

// REDUX
import { useAppSelector } from '../../store';

// PACKAGES
import { Link } from 'react-router-dom';
import { SwitchTransition, CSSTransition } from 'react-transition-group';

// COMPONENTS
import CircleImage from '../../components/CircleImage';
import ScrollToTopButton from '../../components/ScrollToTopButton';
import Button from '../../components/Button';
import LoadingSpinner from '../../components/LoadingSpinner';
import GlobalModal from '../../components/GlobalModal';
import ReinstateFamiliesModal from './ReinstateFamiliesModal';
import DisplayDate from '../../components/DisplayDate';

// ICONS
import { ReactComponent as ValidatedIcon } from '../../assets/svg/shield-color.svg';
import { ReactComponent as InValidatedIcon } from '../../assets/svg/shield-grey.svg';
import { ReactComponent as SeachIcon } from '../../assets/svg/search.svg';

// TYPES
import { FamiliesDashboardFamilyMember, DomainProfileType } from '../../api/models';
type TabStatus = 'parents' | 'students' | 'parent-volunteers' | 'past';

const Families: React.FC = () => {
	// Check if User is Organisation Admin
	const isOrganisationAdmin = useAppSelector((state) => state.user.isOrganisationAdmin);

	// Error and Loading state
	const [formErrorMessage, setFormErrorMessage] = React.useState<string>('');

	const [isLoading, setIsLoading] = React.useState<boolean>(true);

	// Set up data state
	const [familiesData, setFamiliesData] = React.useState<FamiliesDashboardFamilyMember[]>([]);

	// Data used to search, it will not be changed and will always remain as its inital set value
	const [originalFamiliesData, setOriginalFamiliesData] = React.useState<FamiliesDashboardFamilyMember[]>([]);

	const [reinstateFamilyId, setReinstateFamilyId] = React.useState<string>('');

	const [showReinstateFamiliesModal, setShowReinstateFamiliesModal] = React.useState<boolean>(false);

	// State to control active tab and content
	const [activeTab, setActiveTab] = useTabState<TabStatus>('parents', 'tabView');

	const [searchValue, setSearchValue] = React.useState<string>('');

	// function to  Get Families data from api call
	const getFamiliesData = async () => {
		setIsLoading(true);

		const response = await apibridge.getFamiliesData();

		if (response.isError) {
			setIsLoading(false);

			setFormErrorMessage(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);
		} else {
			const { result = [] } = response;

			setIsLoading(false);

			setFamiliesData(result);

			setOriginalFamiliesData(result);
		}
	};

	React.useEffect(() => {
		getFamiliesData();
	}, []);

	const parentsData = familiesData.filter(
		(item) => item.profileType === DomainProfileType.Parent && item.isCurrentMember === true
	);

	const studentData = familiesData.filter(
		(item) => item.profileType === DomainProfileType.Student && item.isCurrentMember === true
	);

	const parentsVolunteersData = parentsData.filter(
		(item) => item.isVolunteer === true && item.isVolunteerValidated === true && item.isCurrentMember === true
	);

	const pastData = familiesData.filter((item) => item.isCurrentMember === false);

	// Filter Function
	const onChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
		const newSearchValue = e.target.value;

		if (!newSearchValue) {
			setFamiliesData(originalFamiliesData);
		} else {
			const newSearchResult = originalFamiliesData.filter(
				(data) =>
					(data.firstName && data.firstName.toLowerCase().includes(newSearchValue.toLowerCase())) ||
					(data.lastName && data.lastName.toLowerCase().includes(newSearchValue.toLowerCase()))
			);

			setFamiliesData(newSearchResult);
		}

		setSearchValue(newSearchValue);
	};

	return (
		<>
			<div className="page-general-layout families-page">
				{!!formErrorMessage ? (
					<div className="form-validation-error">{formErrorMessage}</div>
				) : (
					<SwitchTransition mode="out-in">
						<CSSTransition
							key={`key_${isLoading}`}
							timeout={600}
							classNames="standalone-fade"
							mountOnEnter
							unmountOnExit
						>
							{isLoading ? (
								<LoadingSpinner show={isLoading} />
							) : (
								<>
									<nav className="page-header-section">
										<div className="header-left">
											<Button
												onClick={() => setActiveTab('parents')}
												className={`staff-category pdl-0 ${activeTab === 'parents' ? 'active' : ''}`}
											>
												Parents
											</Button>

											<Button
												onClick={() => setActiveTab('students')}
												className={`staff-category ${activeTab === 'students' ? 'active' : ''}`}
											>
												Children & Students
											</Button>

											<Button
												onClick={() => setActiveTab('parent-volunteers')}
												className={`staff-category ${activeTab === 'parent-volunteers' ? 'active' : ''}`}
											>
												Parent Volunteers
											</Button>

											<Button
												onClick={() => setActiveTab('past')}
												className={`staff-category ${activeTab === 'past' ? 'active' : ''}`}
											>
												Past
											</Button>
										</div>

										<div className="header-right">
											<div className="search-container">
												<SeachIcon className="search-icon" />

												<input onChange={onChangeHandler} type="text" placeholder="Search" value={searchValue} />
											</div>
										</div>
									</nav>

									<section className="info-section mtop-40 mbtom-30">
										{activeTab === 'parents' ? (
											<ul>
												{isOrganisationAdmin &&
													parentsData.map((item) => {
														return (
															<li key={item.id} className="row-container">
																<div className="row detail-row">
																	<div className="col-auto pdl-0 pdr-0 img-container">
																		<CircleImage
																			firstName={item.firstName}
																			lastName={item.lastName}
																			src={item.profileImage}
																			isProfileInComplete={item.needsAttention}
																		/>
																	</div>

																	<div className="col-md-auto full-width info-container align-self-center">
																		{item.isVolunteerValidated ? <ValidatedIcon className="mr-12" /> : null}
																		{item.firstName} {item.lastName}
																	</div>

																	<div className="col-auto pdr-0 link-group">
																		<Link className="btn-text mtop-0 pdr-0" to={`/families/${item.id}`}>
																			View
																		</Link>
																	</div>
																</div>
															</li>
														);
													})}
											</ul>
										) : activeTab === 'students' ? (
											<ul>
												{isOrganisationAdmin &&
													studentData.map((item) => {
														return (
															<li key={item.id} className="row-container">
																<div className="row detail-row">
																	<div className="col-auto pdl-0 pdr-0 img-container">
																		<CircleImage
																			firstName={item.firstName}
																			lastName={item.lastName}
																			src={item.profileImage}
																			isProfileInComplete={item.needsAttention}
																		/>
																	</div>

																	<div className="col-md-auto full-width info-container align-self-center">
																		{item.firstName} {item.lastName}
																	</div>

																	<div className="col-auto pdr-0 link-group">
																		<Link className="btn-text mtop-0 pdr-0" to={`/families/${item.id}`}>
																			View
																		</Link>
																	</div>
																</div>
															</li>
														);
													})}
											</ul>
										) : activeTab === 'parent-volunteers' ? (
											<ul>
												{isOrganisationAdmin &&
													parentsVolunteersData.map((item) => {
														return (
															<li key={item.id} className="row-container">
																<div className="row detail-row">
																	<div className="col-auto pdl-0 pdr-0 img-container">
																		<CircleImage
																			firstName={item.firstName}
																			lastName={item.lastName}
																			src={item.profileImage}
																			isProfileInComplete={item.needsAttention}
																		/>
																	</div>

																	<div className="col-md-auto full-width">
																		<div className="row h-100">
																			<div className="col-12 col-lg-6 info-container align-self-center">
																				{item.firstName} {item.lastName}
																			</div>

																			<div className="col-12 col-lg-6 align-self-center">
																				{item.isVolunteerValidated ? (
																					<div className="validation-message">
																						<ValidatedIcon />
																						Parent Volunteer
																					</div>
																				) : (
																					<div className="validation-message">
																						<InValidatedIcon />
																						Parent Volunteer: Validation Required
																					</div>
																				)}
																			</div>
																		</div>
																	</div>

																	<div className="col-auto pdr-0 link-group">
																		<Link className="btn-text pdr-0" to={`/families/${item.id}`}>
																			View
																		</Link>
																	</div>
																</div>
															</li>
														);
													})}
											</ul>
										) : activeTab === 'past' ? (
											<ul>
												{isOrganisationAdmin &&
													pastData.map((item) => {
														return (
															<li key={item.id} className="row-container">
																<div className="row detail-row">
																	<div className="col-auto pdl-0 pdr-0 img-container">
																		<CircleImage
																			firstName={item.firstName}
																			lastName={item.lastName}
																			src={item.profileImage}
																			isProfileInComplete={item.needsAttention}
																		/>
																	</div>

																	<div className="col-md-auto full-width info-container align-self-center">
																		<div className="row">
																			<div className="col-lg-5 col-xl-4">
																				{item.firstName} {item.lastName}
																			</div>

																			{item?.leftDate && (
																				<>
																					<div className="col-md-auto col-lg-3">
																						<span className="body-small text-grey-4 fw-bold mr-1">Start</span>

																						<span className="fw-medium body-small text-grey-4">
																							<DisplayDate>{item?.joinedDate}</DisplayDate>
																						</span>
																					</div>

																					<div className="col-lg-auto">
																						<span className="body-small text-grey-4 fw-bold mr-1">End</span>

																						<span className="fw-medium body-small text-grey-4">
																							<DisplayDate>{item?.leftDate}</DisplayDate>
																						</span>
																					</div>
																				</>
																			)}
																		</div>
																	</div>

																	<div className="col-auto pdr-0 link-group">
																		<div className="d-flex justify-content-end gap-1">
																			<Button
																				type="button"
																				className="btn-text dark-text"
																				onClick={() => {
																					setReinstateFamilyId(item.id || '');
																					setShowReinstateFamiliesModal(true);
																				}}
																			>
																				Reinstate
																			</Button>

																			<Link className="btn-text pdr-0" to={`/past-families/${item.id}`}>
																				View
																			</Link>
																		</div>
																	</div>
																</div>
															</li>
														);
													})}
											</ul>
										) : null}
									</section>

									<ScrollToTopButton />
								</>
							)}
						</CSSTransition>
					</SwitchTransition>
				)}
			</div>
			<GlobalModal
				isOpen={showReinstateFamiliesModal}
				setIsOpen={setShowReinstateFamiliesModal}
				titleText="Reinstate Family"
			>
				<ReinstateFamiliesModal
					reinstateFamilyId={reinstateFamilyId}
					closeModal={() => setShowReinstateFamiliesModal(false)}
				/>
			</GlobalModal>
		</>
	);
};

export default Families;
